import { Edge } from "@xyflow/react"

/**
 * Creates bidirectional edge relationship lookups from array of edges
 * @param edges - Array of edges containing source and target IDs
 * @returns Object with two lookups:
 *  - childrens: Map of target IDs to array of source IDs
 *  - parents: Map of source IDs to array of target IDs
 * @example
 * const edges = [
 *   { source: "1", target: "2" },
 *   { source: "1", target: "3" }
 * ]
 * createEdgesLookup(edges)
 * // Returns:
 * // {
 * //   childrens: { "2": ["1"], "3": ["1"] },
 * //   parents: { "1": ["2", "3"] }
 * // }
 */
export function createEdgesLookup(edges: Edge[]) {
  const childrens = new Map<Edge["target"], Set<Edge["source"]>>()
  const parents = new Map<Edge["source"], Set<Edge["target"]>>()

  for (const edge of edges) {
    // Build childrens
    const sources = childrens.get(edge.target) ?? new Set()
    sources.add(edge.source)
    childrens.set(edge.target, sources)

    // Build parents
    const targets = parents.get(edge.source) ?? new Set()
    targets.add(edge.target)
    parents.set(edge.source, targets)
  }

  return {
    /** Map of target IDs to array of source IDs */
    childrens: Object.fromEntries(Array.from(childrens, ([k, v]) => [k, Array.from(v)])),
    /** Map of source IDs to array of target IDs */
    parents: Object.fromEntries(Array.from(parents, ([k, v]) => [k, Array.from(v)])),
  }
}
